import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldObject } from '../../../../openapi/atlantis';
import { SigneeDocumentControllerApi } from '../../../../openapi/signature-api';
import {
  AgreementResponse,
  UserAgreementControllerApi,
} from '../../../../openapi/yenta';
import ErrorService from '../../../../services/ErrorService';
import { showErrorToast } from '../../../../slices/ToastNotificationSlice';
import { useAppDispatch, useAppSelector } from '../../../../slices/store';
import { SignatureWidgetModeEnum } from '../../../../types';
import Logger from '../../../../utils/Logger';
import {
  getSignatureApiConfiguration,
  getYentaConfiguration,
} from '../../../../utils/OpenapiConfigurationUtils';
import DefaultLoader from '../../../DefaultLoader';
import SignatureWidget from '../../../inputs/SignatureWidget';

interface SignatureComponentV2Props {
  name: string;
  field: FieldObject;
  documentSignedCallback?: (isSignedAlready: boolean) => void;
  browserMode?: boolean;
}

const SignatureComponentV2: React.FC<SignatureComponentV2Props> = ({
  name,
  field,
  documentSignedCallback,
  browserMode = true,
}) => {
  const dispatch = useAppDispatch();
  const authUserDetail = useAppSelector((state) => state.auth.authUserDetail);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [agreementData, setAgreementData] = useState<AgreementResponse>({});

  const { setValue, setError, clearErrors, trigger } = useFormContext();

  const handleNextButton = useCallback(
    async (agreementResponse: AgreementResponse) => {
      try {
        setError('submit', {
          type: 'manual',
          message: 'Please sign the agreement to proceed',
        });

        const { data } = await new SigneeDocumentControllerApi(
          getSignatureApiConfiguration(),
        ).getDocumentFromToken(agreementResponse.signeeToken!);

        if (data.signed) {
          clearErrors();
          trigger();
          setValue(name, agreementResponse.agreementId!);
        }
      } catch (e: any) {
        Logger.error('Failed to get document data', e, {
          agreementResponse,
        });
        ErrorService.notify('Failed to get document data', e, {
          agreementResponse,
        });
      }
    },
    [clearErrors, name, setError, setValue, trigger],
  );

  const fetchAgreementData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await new UserAgreementControllerApi(
        await getYentaConfiguration(),
      ).createAgreementByType({
        userId: authUserDetail?.id!,
        agreementType: (field as any).agreementType,
        ...((field as any).invitationId && {
          invitationId: (field as any).invitationId,
        }),
      });

      setAgreementData(data);
      await handleNextButton(data);
    } catch (e: any) {
      dispatch(
        showErrorToast(
          'Unable to fetch agreement data. Please try again after sometime.',
        ),
      );
      ErrorService.notifyIgnoreAuthErrors(
        'Unable to fetch user agreement data',
        e,
      );
    } finally {
      setIsLoading(false);
    }
  }, [authUserDetail?.id, dispatch, field, handleNextButton]);

  useEffect(() => {
    if (authUserDetail?.id) fetchAgreementData();
  }, [authUserDetail?.id, fetchAgreementData]);

  const handleDocumentSigned = async () => {
    setIsLoading(true);
    try {
      await new UserAgreementControllerApi(
        await getYentaConfiguration(),
      ).signAgreement({
        userId: authUserDetail?.id!,
        agreementId: agreementData.agreementId!,
      });
      clearErrors();
      trigger();
      setValue(name, agreementData.agreementId!);
      await documentSignedCallback?.(false);
    } catch (e: any) {
      dispatch(
        showErrorToast(
          'Unable to sign agreement. Please try again after sometime.',
        ),
      );
      ErrorService.notifyIgnoreAuthErrors('Unable to sign user agreement', e, {
        agreementData,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <DefaultLoader />;

  return (
    <div className='flex items-center justify-center w-full h-full lg:p-0'>
      <div className='relative w-full'>
        <div className='relative w-full bg-white rounded-2xl'>
          <div
            className='w-full flex flex-col bg-grey-100 h-[calc(100vh-146px)] sm:h-[calc(100vh-250px)]'
            aria-label='sign-agreement-and-manual'
          >
            <SignatureWidget
              token={agreementData.signeeToken!}
              mode={SignatureWidgetModeEnum.SIGN}
              onDocumentSigned={handleDocumentSigned}
              onDocumentAlreadySigned={async () =>
                await documentSignedCallback?.(true)
              }
              showDownload
              showMobileTooltip
              isLoading={isLoading}
              browserMode={browserMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureComponentV2;
